import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { StrengthSessionDialog } from "./strengthProgress/components/StrengthSessionDialog";
import {
  formToTemplateData,
  templateDataToSession,
  type TrainingTemplateData,
} from "./strengthProgress/templateUtils";
import type { StrengthSessionFormData } from "./strengthProgress/types";

export default function TrainingTemplates() {
  const { t } = useTranslation();
  const {
    canReadOwnStrengthProgress,
    canWriteStrengthProgress,
    canManageExercises,
  } = usePermissions();
  const utils = trpc.useUtils();
  const [clientId, setClientId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{
    id: number;
    nazov: string;
    data: TrainingTemplateData;
  } | null>(null);
  const { data: clients = [] } = trpc.klient.list.useQuery(undefined, {
    enabled: canReadOwnStrengthProgress,
  });
  const trainingClients = useMemo(
    () => clients.filter(client => client.odberTrening),
    [clients]
  );
  useEffect(() => {
    if (trainingClients.length === 1) setClientId(trainingClients[0].id);
  }, [trainingClients]);
  const { data: exercises = [] } = trpc.cvik.list.useQuery(
    { klientId: clientId!, includeInactive: canManageExercises },
    { enabled: clientId != null && canReadOwnStrengthProgress }
  );
  const {
    data: templates = [],
    isLoading,
    isError,
  } = trpc.trainingTemplate.list.useQuery(
    { klientId: clientId! },
    { enabled: clientId != null && canWriteStrengthProgress }
  );
  const create = trpc.trainingTemplate.create.useMutation();
  const update = trpc.trainingTemplate.update.useMutation();
  const remove = trpc.trainingTemplate.delete.useMutation();
  const editingSession = useMemo(
    () => (editing ? templateDataToSession(editing.data) : null),
    [editing]
  );
  const invalidate = () => utils.trainingTemplate.list.invalidate();
  const save = async (form: StrengthSessionFormData, name?: string) => {
    if (!clientId || !name) return;
    try {
      const payload = {
        klientId: clientId,
        nazov: name,
        data: formToTemplateData(form),
      };
      if (editing) await update.mutateAsync({ ...payload, id: editing.id });
      else await create.mutateAsync(payload);
      await invalidate();
      setOpen(false);
      setEditing(null);
      toast.success(t("strengthProgress:templates.saved"));
    } catch {
      toast.error(t("strengthProgress:templates.saveFailed"));
    }
  };
  const deleteTemplate = async (id: number) => {
    if (!clientId || !confirm(t("strengthProgress:templates.deleteConfirm")))
      return;
    try {
      await remove.mutateAsync({ klientId: clientId, id });
      await invalidate();
      toast.success(t("strengthProgress:templates.deleted"));
    } catch {
      toast.error(t("strengthProgress:templates.saveFailed"));
    }
  };
  if (!canWriteStrengthProgress) return null;
  return (
    <DashboardLayout>
      <div className="container py-8 space-y-6">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">
              {t("strengthProgress:templates.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("strengthProgress:templates.description")}
            </p>
          </div>
          {canWriteStrengthProgress && (
            <Button
              disabled={!clientId || !exercises.some(e => e.aktivny)}
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("strengthProgress:templates.new")}
            </Button>
          )}
        </div>
        <div className="max-w-md space-y-2">
          <Label>{t("strengthProgress:filters.client")}</Label>
          <Select
            value={clientId?.toString() ?? ""}
            onValueChange={value => {
              setClientId(Number(value));
              setEditing(null);
              setOpen(false);
            }}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t("strengthProgress:filters.chooseClient")}
              />
            </SelectTrigger>
            <SelectContent>
              {trainingClients.map(client => (
                <SelectItem key={client.id} value={String(client.id)}>
                  {client.alias}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!clientId ? (
          <p>{t("strengthProgress:empty.chooseClient.description")}</p>
        ) : isLoading ? (
          <p>{t("common:loading")}</p>
        ) : isError ? (
          <p role="alert">{t("strengthProgress:messages.loadFailed")}</p>
        ) : templates.length === 0 ? (
          <p className="text-muted-foreground">
            {t("strengthProgress:templates.empty")}
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {templates.map(template => (
              <Card key={template.id}>
                <CardHeader className="flex flex-row justify-between gap-2">
                  <CardTitle>{template.nazov}</CardTitle>
                  {canWriteStrengthProgress && (
                    <div className="flex">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t("common:edit")}
                        onClick={() => {
                          setEditing(template);
                          setOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t("common:delete")}
                        onClick={() => deleteTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {template.data.poznamka}
                  </p>
                  <ul className="text-sm space-y-1">
                    {template.data.serie.map((set, index) => (
                      <li key={index}>
                        {exercises.find(exercise => exercise.id === set.cvikId)
                          ?.nazov ?? `#${set.cvikId}`}{" "}
                        · {set.opakovania} × {set.hmotnostKg} kg
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {open && (
          <StrengthSessionDialog
            open={open}
            onOpenChange={setOpen}
            exercises={exercises}
            session={editingSession}
            templateMode
            initialTemplateName={editing?.nazov}
            isSaving={create.isPending || update.isPending}
            onSubmit={save}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
